import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { SortableTableHeader, useSortState, sortData } from '../../components/SortableTableHeader';
import api, { getApiErrorMessage } from '../../services/api';
import { toast } from 'sonner';
import { CheckSquare, X } from 'lucide-react';

export function POApprovalRulesPanel({ order, orderNumber }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  React.useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/api/approvals/po/preview/${orderNumber}`);
        setPreview(r.data);
      } catch (e) {
        setError(getApiErrorMessage(e, 'Failed to load approval rule'));
      }
      setLoading(false);
    })();
  }, [orderNumber]);

  if (loading) return <p className="text-xs text-muted-foreground py-4">Loading approval rule...</p>;
  if (error) return <p className="text-xs text-red-500 py-4">{error}</p>;
  if (!preview?.rule) return <p className="text-xs text-muted-foreground py-4">No approval rule applies to this order.</p>;

  return (
    <div>
      <h3 className="font-bold text-primary mb-2">Approval Rule</h3>
      <POApprovalRuleRow rule={preview.rule} />
    </div>
  );
}

function POApprovalRuleRow({ rule: r }) {
  return (
    <div className="bg-card border rounded-lg p-4 text-xs space-y-2">
      <div className="flex items-center justify-between">
        <span className="font-semibold text-primary">{r.rule_name || r.code}</span>
        <Badge className="text-[9px]">{r.status || 'ACTIVE'}</Badge>
      </div>
      {r.description && <p className="text-muted-foreground">{r.description}</p>}
      <div className="grid grid-cols-3 gap-2">
        <div><span className="text-muted-foreground">Scope:</span> {r.scope || 'All'}</div>
        <div><span className="text-muted-foreground">Threshold:</span> {r.threshold_type || '—'} {r.threshold_value != null ? `(${r.threshold_value})` : ''}</div>
        <div><span className="text-muted-foreground">Levels:</span> {r.levels?.length || r.approver_count || 0}</div>
      </div>
      {r.levels && (
        <div className="mt-2 space-y-1">
          {r.levels.map((lvl, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded bg-muted/30">
              <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold">{i + 1}</span>
              <span>{lvl.approver_name || lvl.approver_email || lvl.approver_id}</span>
              {lvl.threshold && <span className="text-muted-foreground ml-auto">Up to {lvl.threshold}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function POApproversPanel({ order, orderNumber, user, canEdit, onReload, fmtTz }) {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/api/approvals/po/status/${orderNumber}`);
        setApprovals(r.data.approvals || []);
      } catch {}
      setLoading(false);
    })();
  }, [orderNumber]);

  if (loading) return <p className="text-xs text-muted-foreground py-4">Loading approvers...</p>;

  const status = (order.status || '').toUpperCase();
  const isPending = status === 'PENDING';
  const myApproval = approvals.find(a => a.approver_id === user?.user_id && a.status === 'PENDING');

  return (
    <div>
      <h3 className="font-bold text-primary mb-3">Rule Approvers</h3>
      {approvals.length === 0 ? (
        <p className="text-xs text-muted-foreground py-4">No approvers assigned.</p>
      ) : (
        <div className="space-y-2">
          {approvals.map((a, i) => (
            <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-lg border bg-card">
              <span className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">{a.level || i + 1}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{a.approver_name || a.approver_email || a.approver_id}</p>
                <p className="text-[10px] text-muted-foreground">{a.approver_email || ''}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={`text-[9px] ${
                  a.status === 'APPROVED' ? 'bg-green-100 text-green-700' :
                  a.status === 'REJECTED' ? 'bg-red-100 text-red-700' :
                  a.status === 'PENDING' ? 'bg-amber-100 text-amber-700' :
                  'bg-gray-100 text-gray-600'
                }`}>{a.status}</Badge>
                {a.actioned_at && <span className="text-[9px] text-muted-foreground">{fmtTz(a.actioned_at)}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {isPending && myApproval && (
        <POApproveRejectActions orderNumber={orderNumber} onDone={onReload} />
      )}
    </div>
  );
}

function POApproveRejectActions({ orderNumber, onDone }) {
  const [mode, setMode] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleAction = async () => {
    setSubmitting(true);
    try {
      if (mode === 'approve') {
        await api.post(`/api/approvals/po/approve/${orderNumber}`, { notes });
        toast.success('Approved');
      } else {
        if (!notes.trim()) { toast.error('Please provide a reason for rejection'); setSubmitting(false); return; }
        await api.post(`/api/approvals/po/reject/${orderNumber}`, { notes });
        toast.success('Rejected — returned to Unissued');
      }
      setMode(null);
      setNotes('');
      onDone();
    } catch (e) {
      const msg = getApiErrorMessage(e, 'Action failed');
      if (msg.startsWith('Unable to approve')) toast.warning(msg);
      else toast.error(msg);
    }
    setSubmitting(false);
  };

  if (!mode) {
    return (
      <div className="flex gap-2 mt-3 pl-10">
        <Button size="sm" onClick={() => setMode('approve')} className="bg-green-600 hover:bg-green-700" data-testid="po-approve-btn">
          <CheckSquare className="h-3.5 w-3.5 mr-1" /> Approve
        </Button>
        <Button size="sm" variant="destructive" onClick={() => setMode('reject')} data-testid="po-reject-btn">
          <X className="h-3.5 w-3.5 mr-1" /> Reject
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-3 pl-10 space-y-2">
      <p className="text-xs font-semibold">{mode === 'approve' ? 'Approval Notes (optional)' : 'Reason for Rejection *'}</p>
      <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder={mode === 'reject' ? 'Please explain why...' : 'Optional notes...'} className="text-xs" data-testid="po-approval-notes" />
      <div className="flex gap-2">
        <Button size="sm" onClick={handleAction} disabled={submitting}
          className={mode === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
          variant={mode === 'reject' ? 'destructive' : 'default'}
          data-testid="po-confirm-action">
          {submitting ? 'Processing...' : mode === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
        </Button>
        <Button size="sm" variant="outline" onClick={() => { setMode(null); setNotes(''); }}>Cancel</Button>
      </div>
    </div>
  );
}

export function DetailInfoCard({ type, order }) {
  const rows = [];
  if (type === 'originator') {
    rows.push(['Originator', order.buyer_name || order.originator_name || '—']);
    rows.push(['Email', order.originator_email || order.created_by_email || '—']);
    rows.push(['Department', order.department_code || '—']);
    rows.push(['Request Number', order.request_number || '—']);
  } else if (type === 'buyer') {
    rows.push(['Buyer Name', order.buyer_name || '—']);
    rows.push(['Department', order.department_code || '—']);
    rows.push(['Category', order.category_code || '—']);
  } else if (type === 'supplier') {
    rows.push(['Supplier Code', order.supplier_code || '—']);
    rows.push(['Supplier Name', order.supplier_name || '—']);
    rows.push(['Address', order.supplier_address || '—']);
    rows.push(['City', order.supplier_city || '—']);
    rows.push(['State/Province', order.supplier_state || '—']);
    rows.push(['Postal Code', order.supplier_postal || '—']);
    rows.push(['Country', order.supplier_country || '—']);
    rows.push(['Contact', order.supplier_contact || '—']);
    rows.push(['Phone', order.supplier_phone || '—']);
    rows.push(['Email', order.supplier_email || '—']);
    rows.push(['Contract', order.contract_number || '—']);
  } else if (type === 'delivery') {
    rows.push(['Delivery Code', order.delivery_code || '—']);
    rows.push(['Delivery To', order.delivery_to || '—']);
    rows.push(['Address', order.delivery_address || '—']);
    rows.push(['City', order.delivery_city || '—']);
    rows.push(['State/Province', order.delivery_state || '—']);
    rows.push(['Postal Code', order.delivery_postal || '—']);
    rows.push(['Country', order.delivery_country || '—']);
    rows.push(['Instructions', order.instructions || '—']);
    rows.push(['Shipping Method', order.shipping_method || '—']);
  } else if (type === 'invoice') {
    rows.push(['Invoice Code', order.invoice_code || '—']);
    rows.push(['Invoice Address', order.invoice_address || '—']);
    rows.push(['City', order.invoice_city || '—']);
    rows.push(['State/Province', order.invoice_state || '—']);
    rows.push(['Postal Code', order.invoice_postal || '—']);
    rows.push(['Country', order.invoice_country || '—']);
    rows.push(['Payment Terms', order.payment_terms || '—']);
    rows.push(['Incoterms', order.incoterms || '—']);
  }
  return (
    <table className="w-full text-sm">
      <tbody>
        {rows.map(([label, value], i) => (
          <tr key={i} className="border-b">
            <td className="py-2 pr-3 text-muted-foreground font-medium w-36">{label}</td>
            <td className="py-2">{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
